import React, { FunctionComponent, lazy, Suspense } from 'react';
import Icon from '@ant-design/icons';
import { IconComponentProps } from '@ant-design/icons/lib/components/Icon';
import { useColorScheme } from '@mui/material/styles';
import { Theme } from '@monorepo/client-types';
import { IconsNames } from '@monorepo/types';

import './icons.scss';

const Alarm = lazy(() => import('./icons/alarm.icon.svg?react'));
const Android = lazy(() => import('./icons/android.icon.svg?react'));
const Badge = lazy(() => import('./icons/badge.icon.svg?react'));
const BadgeC = lazy(() => import('./icons/badge-c.icon.svg?react'));
const Bank = lazy(() => import('./icons/bank.icon.svg?react'));
const Bell = lazy(() => import('./icons/bell.icon.svg?react'));
const BiDownload = lazy(() => import('./icons/bi-download.icon.svg?react'));
const BlackList = lazy(() => import('./icons/blackList.icon.svg?react'));
const Bolt = lazy(() => import('./icons/bolt.icon.svg?react'));
const Bookmark = lazy(() => import('./icons/bookmark.icon.svg?react'));
const BootcampFivers = lazy(
  () => import('./icons/bootcamp-5ers.icon.svg?react'),
);
const BlankCalendar = lazy(
  () => import('./icons/blank-calendar.icon.svg?react'),
);
const Burn = lazy(() => import('./icons/burn.icon.svg?react'));
const BulletPoint = lazy(() => import('./icons/bullet-point.icon.svg?react'));
const BxChat = lazy(() => import('./icons/bx-chat.icon.svg?react'));
const BxRefresh = lazy(() => import('./icons/bx-refresh.icon.svg?react'));
const Camera = lazy(() => import('./icons/camera.icon.svg?react'));
const CardBadge = lazy(() => import('./icons/card-badge.icon.svg?react'));
const CardBadgeMobile = lazy(
  () => import('./icons/card-badge-mobile.icon.svg?react'),
);
const CaretDown = lazy(() => import('./icons/bx-caret-down.icon.svg?react'));
const CaretUp = lazy(() => import('./icons/bx-caret-up.icon.svg?react'));
const Cash = lazy(() => import('./icons/cash.icon.svg?react'));
const CaretRight = lazy(() => import('./icons/caret-right.icon.svg?react'));
const CertificateCheck = lazy(
  () => import('./icons/certificate-check.icon.svg?react'),
);
const ChartAverage = lazy(() => import('./icons/chart-average.icon.svg?react'));
const ChartClusterBar = lazy(
  () => import('./icons/chart-cluster-bar.icon.svg?react'),
);
const ChartLine = lazy(() => import('./icons/chart-line.icon.svg?react'));
const ChartLineSmooth = lazy(
  () => import('./icons/chart-line-smooth.icon.svg?react'),
);
const ChartLinear = lazy(() => import('./icons/chart-linear.icon.svg?react'));
const ChartPostContest = lazy(
  () => import('./icons/chart-post-contest.icon.svg?react'),
);
const ChartStepper = lazy(() => import('./icons/chart-stepper.icon.svg?react'));
const Chat = lazy(() => import('./icons/chat.icon.svg?react'));
const Check = lazy(() => import('./icons/check.icon.svg?react'));
const CheckCircle = lazy(() => import('./icons/check-circle.icon.svg?react'));
const ExclamationCircle = lazy(
  () => import('./icons/exclamation-circle.icon.svg?react'),
);
const ExclamationTriangle = lazy(
  () => import('./icons/exclamation-triangle.icon.svg?react'),
);
const InfoCircle = lazy(() => import('./icons/info-circle.icon.svg?react'));
const CloseCircle = lazy(() => import('./icons/close-circle.icon.svg?react'));
const CheckTwo = lazy(() => import('./icons/check-two.icon.svg?react'));
const Calendar = lazy(() => import('./icons/calendar.icon.svg?react'));
const Chess = lazy(() => import('./icons/chess.icon.svg?react'));
const ChevronDown = lazy(() => import('./icons/chevron-down.icon.svg?react'));
const ChevronLeft = lazy(() => import('./icons/chevron-left.icon.svg?react'));
const ChevronRight = lazy(() => import('./icons/chevron-right.icon.svg?react'));
const ChevronUp = lazy(() => import('./icons/chevron-up.icon.svg?react'));
const Close = lazy(() => import('./icons/close.icon.svg?react'));
const ClockOutline = lazy(() => import('./icons/clock-outline.icon.svg?react'));
const CloudAuditing = lazy(
  () => import('./icons/cloud-auditing.icon.svg?react'),
);
const CloudMonitoring = lazy(
  () => import('./icons/cloud-monitoring.icon.svg?react'),
);
const CoinBag = lazy(() => import('./icons/coin-bag.icon.svg?react'));
const CoinBagLine = lazy(() => import('./icons/coin-bag-line.icon.svg?react'));
const Compass = lazy(() => import('./icons/compass.icon.svg?react'));
const Contest = lazy(() => import('./icons/contest.icon.svg?react'));
const CpEdit = lazy(() => import('./icons/cp-edit.icon.svg?react'));
const CreateTradeTalkConfirmation = lazy(
  () => import('./icons/share-trade-talk-confirmation.icon.svg?react'),
);
const Credit = lazy(() => import('./icons/credit.icon.svg?react'));
const CreditCardTwoBack = lazy(
  () => import('./icons/credit-card-two-back.icon.svg?react'),
);
const DayTradingTTP = lazy(
  () => import('./icons/day-trading-ttp.icon.svg?react'),
);
const Diamond = lazy(() => import('./icons/diamond.icon.svg?react'));
const Discord = lazy(() => import('./icons/discord.icon.svg?react'));
const Dots = lazy(() => import('./icons/dots.icon.svg?react'));
const Download = lazy(() => import('./icons/download.icon.svg?react'));
const DragAndDrop = lazy(() => import('./icons/drag-drop.icon.svg?react'));
const Duplicate = lazy(() => import('./icons/duplicate.icon.svg?react'));
const Dpad = lazy(() => import('./icons/dpad.icon.svg?react'));
const Earth = lazy(() => import('./icons/earth.icon.svg?react'));
const EarthFilled = lazy(() => import('./icons/earth-filled.icon.svg?react'));
const Edit = lazy(() => import('./icons/edit.icon.svg?react'));
const Ellipse = lazy(() => import('./icons/ellipse.icon.svg?react'));
const Event = lazy(() => import('./icons/event.icon.svg?react'));
const Exchange = lazy(() => import('./icons/exchange.icon.svg?react'));
const ExperienceFivers = lazy(
  () => import('./icons/experience-fivers.icon.svg?react'),
);
const Cart = lazy(() => import('./icons/cart.icon.svg?react'));
const TriangleDown = lazy(() => import('./icons/triangle-down.icon.svg?react'));
const Coupon = lazy(() => import('./icons/coupon.icon.svg?react'));
const ArrowLeftShort = lazy(
  () => import('./icons/arrow-left-short.icon.svg?react'),
);
const Eye = lazy(() => import('./icons/eye.icon.svg?react'));
const EyeClosed = lazy(() => import('./icons/eye-closed.icon.svg?react'));
const Fail = lazy(() => import('./icons/fail.icon.svg?react'));
const Facebook = lazy(() => import('./icons/facebook.icon.svg?react'));
const FacebookNew = lazy(() => import('./icons/facebook-new.icon.svg?react'));
const FiversCredit = lazy(() => import('./icons/fivers-credit.icon.svg?react'));
const Flag = lazy(() => import('./icons/flag.icon.svg?react'));
const FlagV = lazy(() => import('./icons/flag-v.icon.svg?react'));
const Gauge = lazy(() => import('./icons/gauge.icon.svg?react'));
const GalleryExport = lazy(
  () => import('./icons/gallery-export.icon.svg?react'),
);
const GroupAccount = lazy(() => import('./icons/group-account.icon.svg?react'));
const Heart = lazy(() => import('./icons/heart.icon.svg?react'));
const HeartFilled = lazy(() => import('./icons/heart-filled.icon.svg?react'));
const HeartHandshake = lazy(
  () => import('./icons/heart-handshake.icon.svg?react'),
);
const HighStakesFivers = lazy(
  () => import('./icons/high-stakes-5ers.icon.svg?react'),
);
const Horse = lazy(() => import('./icons/horse.icon.svg?react'));
const HourGlass = lazy(() => import('./icons/hourglass.icon.svg?react'));
const HyperGrowthFivers = lazy(
  () => import('./icons/hyper-growth-5ers.icon.svg?react'),
);
const Hemisphere = lazy(() => import('./icons/hemisphere.icon.svg?react'));
const Instagram = lazy(() => import('./icons/instagram.icon.svg?react'));
const InvoiceOutline = lazy(
  () => import('./icons/invoice-outline.icon.svg?react'),
);
const iOS = lazy(() => import('./icons/ios.icon.svg?react'));
const LayoutDashboard = lazy(
  () => import('./icons/layout-dashboard.icon.svg?react'),
);
const Lightning = lazy(() => import('./icons/lightning.icon.svg?react'));
const LightbulbFilament = lazy(
  () => import('./icons/lightbulb-filament.icon.svg?react'),
);
const Link = lazy(() => import('./icons/link.icon.svg?react'));
const Linkedin = lazy(() => import('./icons/linkedin.icon.svg?react'));
const Linux = lazy(() => import('./icons/linux.icon.svg?react'));
const LoadMore = lazy(() => import('./icons/load-more.icon.svg?react'));
const Lock = lazy(() => import('./icons/lock.icon.svg?react'));
const Mac = lazy(() => import('./icons/mac.icon.svg?react'));
const Megaphone = lazy(() => import('./icons/megaphone.icon.svg?react'));
const MegaphoneNew = lazy(() => import('./icons/megaphone-new.icon.svg?react'));
const MenuBurger = lazy(() => import('./icons/menu-burger.icon.svg?react'));
const MenuBurgerClose = lazy(
  () => import('./icons/menu-burger-close.icon.svg?react'),
);
const MagnifyingGlass = lazy(
  () => import('./icons/magnifying-glass.icon.svg?react'),
);
const ModalPopup = lazy(() => import('./icons/modal-popup.icon.svg?react'));
const MoonStars = lazy(() => import('./icons/moon-stars.icon.svg?react'));
const MountainTop = lazy(() => import('./icons/mountain-top.icon.svg?react'));
const MostPopular = lazy(() => import('./icons/most-popular.icon.svg?react'));
const NotFound = lazy(() => import('./icons/not-found.icon.svg?react'));
const NotRequired = lazy(() => import('./icons/not-required.icon.svg?react'));
const NotVerified = lazy(() => import('./icons/not-verified.icon.svg?react'));
const NoSearchResult = lazy(
  () => import('./icons/no-search-result.icon.svg?react'),
);
const OrderedList = lazy(() => import('./icons/list-ordered.icon.svg?react'));
const PCredit = lazy(() => import('./icons/p-credit.icon.svg?react'));
const Password = lazy(() => import('./icons/password.icon.svg?react'));
const People = lazy(() => import('./icons/people.icon.svg?react'));
const PeopleGroup = lazy(() => import('./icons/people-group.icon.svg?react'));
const Pencil = lazy(() => import('./icons/pencil.icon.svg?react'));
const Person = lazy(() => import('./icons/person.icon.svg?react'));
const PersonTwo = lazy(() => import('./icons/person-two.icon.svg?react'));
const Phone = lazy(() => import('./icons/phone.icon.svg?react'));
const PhoneCare = lazy(() => import('./icons/phone-care.icon.svg?react'));
const Play = lazy(() => import('./icons/play.icon.svg?react'));
const PlayCircle = lazy(() => import('./icons/play-circle.svg?react'));
const Plus = lazy(() => import('./icons/plus.icon.svg?react'));
const PlusDiamond = lazy(() => import('./icons/plus-diamond.icon.svg?react'));
const Plug = lazy(() => import('./icons/plug.icon.svg?react'));
const PodiumRibbon = lazy(() => import('./icons/podium-ribbon.icon.svg?react'));
const Publish = lazy(() => import('./icons/publish.svg?react'));

const User = lazy(() => import('./icons/user.icon.svg?react'));
const PublishPen = lazy(() => import('./icons/publish-pen.icon.svg?react'));

const QuestionCircle = lazy(
  () => import('./icons/question-circle.icon.svg?react'),
);
const Recycle = lazy(() => import('./icons/recycle.icon.svg?react'));
const Refresh = lazy(() => import('./icons/refresh.icon.svg?react'));
const Retry = lazy(() => import('./icons/retry.icon.svg?react'));
const Rejected = lazy(() => import('./icons/rejected.svg?react'));
const Rocket = lazy(() => import('./icons/rocket.icon.svg?react'));
const Basecamp = lazy(() => import('./icons/basecamp.icon.svg?react'));
const BasecampDark = lazy(() => import('./icons/basecamp-dark.icon.svg?react'));
const ZeroLimits = lazy(() => import('./icons/zero-limits.icon.svg?react'));
const ZeroLimitsDark = lazy(
  () => import('./icons/zero-limits-dark.icon.svg?react'),
);
const Room = lazy(() => import('./icons/room.icon.svg?react'));
const Rules = lazy(() => import('./icons/rules.icon.svg?react'));
const RuleThePoolTTP = lazy(
  () => import('./icons/rule-the-pool-ttp.icon.svg?react'),
);
const Search = lazy(() => import('./icons/search.icon.svg?react'));
const Shield = lazy(() => import('./icons/shield.icon.svg?react'));
const ShieldWithTickMark = lazy(
  () => import('./icons/security-check.icon.svg?react'),
);
const Share = lazy(() => import('./icons/share.icon.svg?react'));
const ShareNew = lazy(() => import('./icons/share-new.icon.svg?react'));
const SmileRatingFive = lazy(
  () => import('./icons/smile-rating-5.icon.svg?react'),
);
const SmileRatingFour = lazy(
  () => import('./icons/smile-rating-4.icon.svg?react'),
);
const SmileRatingOne = lazy(
  () => import('./icons/smile-rating-1.icon.svg?react'),
);
const SmileRatingThree = lazy(
  () => import('./icons/smile-rating-3.icon.svg?react'),
);
const SmileRatingTwo = lazy(
  () => import('./icons/smile-rating-2.icon.svg?react'),
);
const Snow = lazy(() => import('./icons/snow.icon.svg?react'));
const Sparkle = lazy(() => import('./icons/sparkle.icon.svg?react'));
const Sparkles = lazy(() => import('./icons/sparkles.icon.svg?react'));
const Star = lazy(() => import('./icons/star.icon.svg?react'));
const StepArrow = lazy(() => import('./icons/step-arrow.icon.svg?react'));
const Sun = lazy(() => import('./icons/sun.icon.svg?react'));
const SwingTTP = lazy(() => import('./icons/swing-ttp.icon.svg?react'));
const TableColumnVisibility = lazy(
  () => import('./icons/column-visibility.icon.svg?react'),
);
const TableFilter = lazy(() => import('./icons/filter.icon.svg?react'));
const TableFullScreen = lazy(
  () => import('./icons/full-screen.icon.svg?react'),
);
const TableNoColumnVisibility = lazy(
  () => import('./icons/no-column-visibility.icon.svg?react'),
);
const TableNoFilter = lazy(() => import('./icons/no-filter.icon.svg?react'));
const TableNoFullScreen = lazy(
  () => import('./icons/no-full-screen.icon.svg?react'),
);
const TableNoSearch = lazy(() => import('./icons/no-search.icon.svg?react'));
const TableRowSizeLarge = lazy(
  () => import('./icons/row-size-large.icon.svg?react'),
);
const TableRowSizeMedium = lazy(
  () => import('./icons/row-size-medium.icon.svg?react'),
);
const TableRowSizeSmall = lazy(
  () => import('./icons/row-size-small.icon.svg?react'),
);
const TableSearch = lazy(() => import('./icons/search-table.icon.svg?react'));
const ThreeDots = lazy(() => import('./icons/three-dots.icon.svg?react'));
const Tiktok = lazy(() => import('./icons/tiktok.icon.svg?react'));
const Trash = lazy(() => import('./icons/trash.icon.svg?react'));
const TrendingDown = lazy(() => import('./icons/trending-down.icon.svg?react'));
const TrendingUp = lazy(() => import('./icons/trending-up.icon.svg?react'));
const Trophy = lazy(() => import('./icons/trophy.icon.svg?react'));
const Twitter = lazy(() => import('./icons/twitter.icon.svg?react'));
const TypeBold = lazy(() => import('./icons/type-bold.icon.svg?react'));
const TypeItalic = lazy(() => import('./icons/type-italic.icon.svg?react'));
const TypeStrikethrough = lazy(
  () => import('./icons/type-strikethrough.icon.svg?react'),
);
const TypeUnderline = lazy(
  () => import('./icons/type-underline.icon.svg?react'),
);
const TextAlignLeft = lazy(
  () => import('./icons/text-align-left.icon.svg?react'),
);
const TextAlignCenter = lazy(
  () => import('./icons/text-align-center.icon.svg?react'),
);
const TextAlignRight = lazy(
  () => import('./icons/text-align-right.icon.svg?react'),
);
const TextAlignJustify = lazy(
  () => import('./icons/text-align-justify.icon.svg?react'),
);
const Umbrella = lazy(() => import('./icons/umbrella.icon.svg?react'));
const UnderReview = lazy(() => import('./icons/under_review.svg?react'));
const Union = lazy(() => import('./icons/union.icon.svg?react'));
const UnorderedList = lazy(
  () => import('./icons/list-unordered.icon.svg?react'),
);
const UpToDate = lazy(() => import('./icons/up-to-date.icon.svg?react'));
const Validation = lazy(() => import('./icons/validation.icon.svg?react'));
const VerificationBadge = lazy(
  () => import('./icons/verification-badge.icon.svg?react'),
);
const Warning = lazy(() => import('./icons/warning.icon.svg?react'));
const Web = lazy(() => import('./icons/web.icon.svg?react'));
const Whatsapp = lazy(() => import('./icons/whatsapp.icon.svg?react'));
const Windows = lazy(() => import('./icons/windows.icon.svg?react'));
const Youtube = lazy(() => import('./icons/youtube.icon.svg?react'));

const TableEdit = lazy(() => import('./icons/table-edit.icon.svg?react'));
const TableDelete = lazy(() => import('./icons/table-delete.icon.svg?react'));
const Stop = lazy(() => import('./icons/stop.icon.svg?react'));

const FlagModal = lazy(() => import('./icons/flag-modal.icon.svg?react'));

type CustomIconComponentProps = Omit<IconComponentProps, 'ref'>;

const IconsMap = {
  [IconsNames.Alarm]: Alarm,
  [IconsNames.Android]: Android,
  [IconsNames.Badge]: Badge,
  [IconsNames.BadgeC]: BadgeC,
  [IconsNames.Bank]: Bank,
  [IconsNames.Bell]: Bell,
  [IconsNames.BlankCalendar]: BlankCalendar,
  [IconsNames.BiDownload]: BiDownload,
  [IconsNames.BlackList]: BlackList,
  [IconsNames.Bolt]: Bolt,
  [IconsNames.Bookmark]: Bookmark,
  [IconsNames.BootcampFivers]: BootcampFivers,
  [IconsNames.BulletPoint]: BulletPoint,
  [IconsNames.Burn]: Burn,
  [IconsNames.BxChat]: BxChat,
  [IconsNames.BxRefresh]: BxRefresh,
  [IconsNames.Camera]: Camera,
  [IconsNames.CardBadge]: CardBadge,
  [IconsNames.CardBadgeMobile]: CardBadgeMobile,
  [IconsNames.CaretDown]: CaretDown,
  [IconsNames.CaretUp]: CaretUp,
  [IconsNames.CaretRight]: CaretRight,
  [IconsNames.Cash]: Cash,
  [IconsNames.ChartLinear]: ChartLinear,
  [IconsNames.CertificateCheck]: CertificateCheck,
  [IconsNames.ChartAverage]: ChartAverage,
  [IconsNames.Calendar]: Calendar,
  [IconsNames.ChartClusterBar]: ChartClusterBar,
  [IconsNames.ChartLine]: ChartLine,
  [IconsNames.ChartLineSmooth]: ChartLineSmooth,
  [IconsNames.ChartPostContest]: ChartPostContest,
  [IconsNames.ChartStepper]: ChartStepper,
  [IconsNames.Chat]: Chat,
  [IconsNames.Check]: Check,
  [IconsNames.CheckCircle]: CheckCircle,
  [IconsNames.ExclamationCircle]: ExclamationCircle,
  [IconsNames.ExclamationTriangle]: ExclamationTriangle,
  [IconsNames.CloseCircle]: CloseCircle,
  [IconsNames.InfoCircle]: InfoCircle,
  [IconsNames.CheckTwo]: CheckTwo,
  [IconsNames.Chess]: Chess,
  [IconsNames.ChevronDown]: ChevronDown,
  [IconsNames.ChevronLeft]: ChevronLeft,
  [IconsNames.ChevronRight]: ChevronRight,
  [IconsNames.ChevronUp]: ChevronUp,
  [IconsNames.Close]: Close,
  [IconsNames.CloudAuditing]: CloudAuditing,
  [IconsNames.CloudMonitoring]: CloudMonitoring,
  [IconsNames.ClockOutline]: ClockOutline,
  [IconsNames.CoinBag]: CoinBag,
  [IconsNames.CoinBagLine]: CoinBagLine,
  [IconsNames.Compass]: Compass,
  [IconsNames.Contest]: Contest,
  [IconsNames.CpEdit]: CpEdit,
  [IconsNames.CreateTradeTalkConfirmation]: CreateTradeTalkConfirmation,
  [IconsNames.Credit]: Credit,
  [IconsNames.CreditCardTwoBack]: CreditCardTwoBack,
  [IconsNames.DayTradingTTP]: DayTradingTTP,
  [IconsNames.Diamond]: Diamond,
  [IconsNames.Discord]: Discord,
  [IconsNames.Dots]: Dots,
  [IconsNames.DragAndDrop]: DragAndDrop,
  [IconsNames.Download]: Download,
  [IconsNames.Dpad]: Dpad,
  [IconsNames.Duplicate]: Duplicate,
  [IconsNames.Earth]: Earth,
  [IconsNames.EarthFilled]: EarthFilled,
  [IconsNames.Edit]: Edit,
  [IconsNames.Ellipse]: Ellipse,
  [IconsNames.Event]: Event,
  [IconsNames.Exchange]: Exchange,
  [IconsNames.ExperienceFivers]: ExperienceFivers,
  [IconsNames.Eye]: Eye,
  [IconsNames.EyeClosed]: EyeClosed,
  [IconsNames.PodiumRibbon]: PodiumRibbon,
  [IconsNames.PlusDiamond]: PlusDiamond,
  [IconsNames.Publish]: Publish,
  [IconsNames.PublishPen]: PublishPen,
  [IconsNames.Link]: Link,
  [IconsNames.Cart]: Cart,
  [IconsNames.TriangleDown]: TriangleDown,
  [IconsNames.Coupon]: Coupon,
  [IconsNames.ArrowLeftShort]: ArrowLeftShort,
  [IconsNames.Share]: Share,
  [IconsNames.ShareNew]: ShareNew,
  [IconsNames.Fail]: Fail,
  [IconsNames.Facebook]: Facebook,
  [IconsNames.FacebookNew]: FacebookNew,
  [IconsNames.FiversCredit]: FiversCredit,
  [IconsNames.Flag]: Flag,
  [IconsNames.FlagV]: FlagV,
  [IconsNames.GalleryExport]: GalleryExport,
  [IconsNames.Gauge]: Gauge,
  [IconsNames.GroupAccount]: GroupAccount,
  [IconsNames.Heart]: Heart,
  [IconsNames.HeartFilled]: HeartFilled,
  [IconsNames.HeartHandshake]: HeartHandshake,
  [IconsNames.HighStakesFivers]: HighStakesFivers,
  [IconsNames.Horse]: Horse,
  [IconsNames.Hemisphere]: Hemisphere,
  [IconsNames.HourGlass]: HourGlass,
  [IconsNames.HyperGrowthFivers]: HyperGrowthFivers,
  [IconsNames.Instagram]: Instagram,
  [IconsNames.InvoiceOutline]: InvoiceOutline,
  [IconsNames.iOS]: iOS,
  [IconsNames.LayoutDashboard]: LayoutDashboard,
  [IconsNames.Lightning]: Lightning,
  [IconsNames.LightbulbFilament]: LightbulbFilament,
  [IconsNames.Linkedin]: Linkedin,
  [IconsNames.Linux]: Linux,
  [IconsNames.LoadMore]: LoadMore,
  [IconsNames.Lock]: Lock,
  [IconsNames.Mac]: Mac,
  [IconsNames.Megaphone]: Megaphone,
  [IconsNames.MegaphoneNew]: MegaphoneNew,
  [IconsNames.MenuBurger]: MenuBurger,
  [IconsNames.MenuBurgerClose]: MenuBurgerClose,
  [IconsNames.ModalPopup]: ModalPopup,
  [IconsNames.MoonStars]: MoonStars,
  [IconsNames.MostPopular]: MostPopular,
  [IconsNames.MountainTop]: MountainTop,
  [IconsNames.MagnifyingGlass]: MagnifyingGlass,
  [IconsNames.NotFound]: NotFound,
  [IconsNames.NotRequired]: NotRequired,
  [IconsNames.NoSearchResult]: NoSearchResult,
  [IconsNames.NotVerified]: NotVerified,
  [IconsNames.OrderedList]: OrderedList,
  [IconsNames.Password]: Password,
  [IconsNames.PCredit]: PCredit,
  [IconsNames.People]: People,
  [IconsNames.PeopleGroup]: PeopleGroup,
  [IconsNames.Pencil]: Pencil,
  [IconsNames.Person]: Person,
  [IconsNames.PersonTwo]: PersonTwo,
  [IconsNames.Phone]: Phone,
  [IconsNames.PhoneCare]: PhoneCare,
  [IconsNames.Play]: Play,
  [IconsNames.PlayCircle]: PlayCircle,
  [IconsNames.Plus]: Plus,
  [IconsNames.Plug]: Plug,
  [IconsNames.QuestionCircle]: QuestionCircle,
  [IconsNames.Recycle]: Recycle,
  [IconsNames.Refresh]: Refresh,
  [IconsNames.Retry]: Retry,
  [IconsNames.Rejected]: Rejected,
  [IconsNames.Rocket]: Rocket,
  [IconsNames.Basecamp]: Basecamp,
  [IconsNames.ZeroLimits]: ZeroLimits,
  [IconsNames.Room]: Room,
  [IconsNames.Rules]: Rules,
  [IconsNames.RuleThePoolTTP]: RuleThePoolTTP,
  [IconsNames.Search]: Search,
  [IconsNames.Shield]: Shield,
  [IconsNames.ShieldWithTickMark]: ShieldWithTickMark,
  [IconsNames.SmileRatingFive]: SmileRatingFive,
  [IconsNames.SmileRatingFour]: SmileRatingFour,
  [IconsNames.SmileRatingOne]: SmileRatingOne,
  [IconsNames.SmileRatingThree]: SmileRatingThree,
  [IconsNames.SmileRatingTwo]: SmileRatingTwo,
  [IconsNames.Snow]: Snow,
  [IconsNames.Sparkle]: Sparkle,
  [IconsNames.Sparkles]: Sparkles,
  [IconsNames.Star]: Star,
  [IconsNames.StepArrow]: StepArrow,
  [IconsNames.Sun]: Sun,
  [IconsNames.SwingTTP]: SwingTTP,
  [IconsNames.TableColumnVisibility]: TableColumnVisibility,
  [IconsNames.TableFilter]: TableFilter,
  [IconsNames.TableFullScreen]: TableFullScreen,
  [IconsNames.TableNoColumnVisibility]: TableNoColumnVisibility,
  [IconsNames.TableNoFilter]: TableNoFilter,
  [IconsNames.TableNoFullScreen]: TableNoFullScreen,
  [IconsNames.TableNoSearch]: TableNoSearch,
  [IconsNames.TableRowSizeLarge]: TableRowSizeLarge,
  [IconsNames.TableRowSizeMedium]: TableRowSizeMedium,
  [IconsNames.TableRowSizeSmall]: TableRowSizeSmall,
  [IconsNames.TableSearch]: TableSearch,
  [IconsNames.ThreeDots]: ThreeDots,
  [IconsNames.Tiktok]: Tiktok,
  [IconsNames.Trash]: Trash,
  [IconsNames.TrendingDown]: TrendingDown,
  [IconsNames.TrendingUp]: TrendingUp,
  [IconsNames.Trophy]: Trophy,
  [IconsNames.Twitter]: Twitter,
  [IconsNames.TypeBold]: TypeBold,
  [IconsNames.TypeItalic]: TypeItalic,
  [IconsNames.TypeStrikethrough]: TypeStrikethrough,
  [IconsNames.TypeUnderline]: TypeUnderline,
  [IconsNames.TextAlignLeft]: TextAlignLeft,
  [IconsNames.TextAlignCenter]: TextAlignCenter,
  [IconsNames.TextAlignRight]: TextAlignRight,
  [IconsNames.TextAlignJustify]: TextAlignJustify,
  [IconsNames.Umbrella]: Umbrella,
  [IconsNames.UnderReview]: UnderReview,
  [IconsNames.Union]: Union,
  [IconsNames.UnorderedList]: UnorderedList,
  [IconsNames.UpToDate]: UpToDate,
  [IconsNames.Validation]: Validation,
  [IconsNames.VerificationBadge]: VerificationBadge,
  [IconsNames.Warning]: Warning,
  [IconsNames.Web]: Web,
  [IconsNames.Whatsapp]: Whatsapp,
  [IconsNames.Windows]: Windows,
  [IconsNames.Youtube]: Youtube,
  [IconsNames.TableEdit]: TableEdit,
  [IconsNames.TableDelete]: TableDelete,
  [IconsNames.Stop]: Stop,
  [IconsNames.FlagModal]: FlagModal,
  [IconsNames.User]: User,
};

interface Props extends CustomIconComponentProps {
  iconName: IconsNames;
  asEmbedded?: boolean;
  disabled?: boolean;
}

export const Icons: FunctionComponent<Props> = ({
  iconName,
  color,
  asEmbedded,
  disabled,
  ...props
}) => {
  const { mode } = useColorScheme();
  const isDarkMode = mode === Theme.Dark;

  let IconComponent = IconsMap[iconName];

  if (isDarkMode) {
    if (iconName === IconsNames.Basecamp) {
      IconComponent = BasecampDark;
    } else if (iconName === IconsNames.ZeroLimits) {
      IconComponent = ZeroLimitsDark;
    }
  }

  return (
    <Suspense>
      {asEmbedded ? (
        <div
          className={`the5ers-icon ${props.className} ${
            disabled ? 'the5ers-icon--disabled' : ''
          }`}
        >
          <IconComponent className="the5ers-icon--shadow" />
        </div>
      ) : (
        <Icon
          style={{
            color: color,
          }}
          component={IconComponent}
          {...props}
        />
      )}
    </Suspense>
  );
};
